














































































































import { Ability } from '@casl/ability'
import { mixins } from 'vue-class-component'
import { Vue, Component } from 'vue-property-decorator'
import { Location, Route } from 'vue-router'

import BookingDates from '@/components/BookingDates.vue'
import BookingExtras from '@/components/BookingExtras.vue'
import BookingInfoCard from '@/components/BookingInfoCard.vue'
import BookingPassenger from '@/components/BookingPassenger.vue'
import BookingPassengersAndPayments from '@/components/BookingPassengersAndPayments.vue'
import BookingPayments from '@/components/BookingPayments.vue'
import BookingQuickLinks from '@/components/BookingQuickLinks.vue'
import BookingStatusBar from '@/components/BookingStatusBar.vue'
import BookingTopNav from '@/components/BookingTopNav.vue'
import FbLoginMixin from '@/components/FbLoginMixin.vue'
import LoadingPlaceholder from '@/components/LoadingPlaceholder.vue'
import PaymentModal from '@/components/modals/PaymentModal.vue'
import VisaLetterModal from '@/components/modals/VisaLetterModal/VisaLetterModal.vue'

import BookingModel from '@/models/booking'
import BookingExtra from '@/models/booking-extra'
import BookingStatus from '@/models/booking-status'
import { BookingStatusBarTypes } from '@/models/booking-status-bar'
import Customer from '@/models/customer'
import Passenger, {
  TransportSuboptions,
  PassengerInfoStatuses,
  PassengerStatuses,
} from '@/models/passenger'
import { VisaLetterData } from '@/models/visa-letter-data'

import getBookingAbilitiesFor from '@/abilities/booking/customer_abilities'
import dayjs, { Dayjs } from '@/dayjs'
import BookingHeaderTitleMixin from '@/mixins/BookingHeaderTitleMixin.vue'
import { GlobalMutationTypes } from '@/store'
import { BookingExtrasGetters } from '@/store/modules/bookingExtras'
import {
  BookingsActionTypes,
  BookingsMutationTypes,
} from '@/store/modules/bookings'
import { CreditCardActionTypes } from '@/store/modules/credit-cards'
import { PassengersActionTypes } from '@/store/modules/passengers'
import { PaymentModalMutationTypes } from '@/store/modules/paymentModal'
import { UtilityGetters } from '@/store/modules/utility'
import {
  VisaLetterModalMutationTypes,
  VisaLetterModalActionTypes,
} from '@/store/modules/visaLetterModal'

@Component({
  components: {
    BookingTopNav,
    BookingInfoCard,
    BookingDates,
    BookingQuickLinks,
    BookingStatusBar,
    BookingPassenger,
    BookingPassengersAndPayments,
    BookingPayments,
    BookingExtras,
    PaymentModal,
    VisaLetterModal,
    LoadingPlaceholder,
  },
})
export default class Booking extends mixins(
  FbLoginMixin,
  BookingHeaderTitleMixin,
) {
  mounted() {
    this.initialSetup(this.$route.params.id)
  }

  beforeRouteUpdate(to: Route, from: Route, next: (to?: Location) => void) {
    this.initialSetup(to.params.id)
    next()
  }

  initialSetup(bookingId: string) {
    this.$store.commit(BookingsMutationTypes.GetStarted)
    this.$store.dispatch(GlobalMutationTypes.ResetBookingRelatedModules)
    this.$store.dispatch(VisaLetterModalActionTypes.GetData, bookingId)
    this.$store.dispatch(CreditCardActionTypes.Query, bookingId)
    this.$store.dispatch(BookingsActionTypes.Get, bookingId).then(() => {
      this.$store.dispatch(BookingsActionTypes.GetStatusBar, bookingId)
      this.gaCheckoutEvent()
    })
  }

  get booking(): BookingModel {
    return this.$store.getters.getBooking(this.$route.params.id)
  }

  get bookingLoaded(): boolean {
    return this.$store.state.bookings.bookingsLoaded
  }

  get bookingStatusBarLoaded(): boolean {
    return this.$store.state.bookings.bookingStatusBarLoaded
  }

  get isStatusBarShown(): boolean {
    return (
      !!this.booking?.bookingStatusBar?.type &&
      this.isPredepartureInfoShownAndValid
    )
  }

  get isPredepartureInfoShownAndValid(): boolean {
    if (!this.booking) {
      return false
    }

    if (
      this.booking.bookingStatusBar.type !==
      BookingStatusBarTypes.PredepartureInformationAvailable
    ) {
      return true
    }

    return this.showPreDepartureInfo
  }

  get passengers(): Passenger[] {
    if (!this.booking) {
      return []
    }

    const passengers = this.$store.getters.getPassengersByIds(
      this.booking.passengerIds,
    )
    this.setPassengerPersonalInfoStatus(passengers)

    return [...passengers]
  }

  get bookingExtras(): BookingExtra[] {
    if (!this.booking) {
      return []
    }

    return this.$store.getters[
      BookingExtrasGetters.GetBookingExtrasByBookingId
    ](this.booking.id)
  }

  get currentCustomer(): Customer {
    return this.$store.getters.getCurrentCustomer
  }

  get currentCustomersPassenger(): Passenger | undefined {
    return this.passengers.find(
      (passenger: Passenger) =>
        passenger.customerId === this.currentCustomer.id,
    )
  }

  get sortedPassengers(): Passenger[] {
    // Scroll down to sorting directions for better understanding
    return this.$_.orderBy(
      this.passengers,
      [
        (p) => p.customerId === this.booking.customerId,
        (p) => p.passengerStatus === PassengerStatuses.Cancelled,
        (p) => {
          if (
            p.passengerStatus !== PassengerStatuses.Cancelled &&
            p.personalInfoStatus === PassengerInfoStatuses.Empty &&
            p.amountPaidCents === 0
          ) {
            return 1
          } else {
            return 0
          }
        },
        (p) => p.passengerStatus === PassengerStatuses.Confirmed,
        (p) =>
          p.passengerStatus !== PassengerStatuses.Cancelled &&
          p.amountPaidCents === 0,
        (p) => {
          switch (p.personalInfoStatus) {
            case PassengerInfoStatuses.Complete:
              return 0
            case PassengerInfoStatuses.Partial:
              return 1
            case PassengerInfoStatuses.Empty:
              return 2
            default:
              return 999
          }
        },
        (p) => {
          switch (p.deck) {
            case TransportSuboptions.AboveDeck:
              return 0
            case TransportSuboptions.LowerDeck:
              return 1
            default:
              return 999
          }
        },
      ],
      // NESTED SORTING RULES
      // TIP: To get a better idea remember that each next rule //
      // is applied on groups created by the previous ones //
      [
        'desc', // Lead first
        'asc', // Cancelled last
        'asc', // Empty AND not paid last
        'desc', // Not confirmed last
        'asc', // Not paid last
        'asc', // Sort by personal info completion
        'asc', // Below deck last
      ],
    )
  }

  get customerBookingAbility(): Ability {
    return getBookingAbilitiesFor(this.currentCustomer)
  }

  get visaLetterData(): VisaLetterData | null {
    return this.$store.getters.visaLetterData
  }

  get showVisaLetter(): boolean {
    return (
      !!this.visaLetterData &&
      !!this.currentCustomersPassenger &&
      this.currentCustomersPassenger.passengerStatus ===
        PassengerStatuses.Confirmed
    )
  }

  get hasPassengerAccessPermission(): boolean {
    const passenger = this.currentCustomersPassenger
    if (!passenger) {
      return false
    }

    const confirmed = passenger.passengerStatus === PassengerStatuses.Confirmed

    let fullyPaid
    if (this.booking.paymentAllocationEnabled) {
      fullyPaid = passenger.amountPaidCents >= passenger.totalPriceCents
    } else {
      fullyPaid = this.booking.paidCents >= this.booking.totalCents
    }

    return confirmed && fullyPaid
  }

  get showPreDepartureInfo(): boolean {
    return (
      this.hasPassengerAccessPermission &&
      this.booking.direction.preDepartureInfo.length > 0
    )
  }

  get showFacebookGroup(): boolean {
    const passenger = this.currentCustomersPassenger
    const displayDate = dayjs(this.currentDate).add(6, 'months')

    return (
      !!passenger &&
      passenger.passengerStatus === PassengerStatuses.Confirmed &&
      displayDate.isAfter(this.booking.departAt) &&
      this.booking.direction.facebookGroup.length > 0
    )
  }

  get currentDate(): Dayjs {
    return this.$store.getters[UtilityGetters.CurrentDate] || dayjs.utc()
  }

  get passengersPlaceholder() {
    if (window.innerWidth > 992) {
      return 'ContentLoaderBookingPassenger'
    } else {
      return 'ContentLoaderBookingPassengerMobile'
    }
  }

  get isBookingCancelled() {
    return this.booking.status === BookingStatus.CANCELLED
  }

  isPassengerGuest(passenger: Passenger): boolean {
    return (
      passenger.personalInfoStatus === PassengerInfoStatuses.Empty &&
      !passenger.amountPaidCents
    )
  }

  onPassengerUpdate(updatedPassenger: Passenger) {
    this.$store.dispatch(PassengersActionTypes.Update, updatedPassenger)
  }

  onResendInvitation(passengerId: number) {
    this.$store.dispatch(PassengersActionTypes.ResendInvitation, passengerId)
  }

  async onPassengerRemove(passengerId: number) {
    await this.$store.dispatch(PassengersActionTypes.Remove, {
      passengerId,
      bookingId: this.booking.id,
    })
    await this.$store.dispatch(PassengersActionTypes.Get, {
      passengerId,
      bookingId: this.booking.id,
    })
  }

  onPaymentModalOpen() {
    this.$store.commit(PaymentModalMutationTypes.Open)
    this.$router.push({ name: 'newPayment', params: { id: this.booking.id } })
  }

  onVisaLetterModalOpen() {
    this.$store.commit(VisaLetterModalMutationTypes.Open)
  }

  onVisaLetterModalClose() {
    this.$store.commit(VisaLetterModalMutationTypes.Close)
  }

  setPassengerPersonalInfoStatus(passengers: Passenger[]): void {
    passengers.forEach((passenger) => {
      const attributeNames = ['title', 'fullName', 'age', 'nationality']
      const attributes = this.$_.chain(passenger).pick(attributeNames).values()

      if (attributes.every((val) => !!val).value()) {
        passenger.personalInfoStatus = PassengerInfoStatuses.Complete
      } else if (attributes.every((val) => !val).value()) {
        passenger.personalInfoStatus = PassengerInfoStatuses.Empty
      } else {
        passenger.personalInfoStatus = PassengerInfoStatuses.Partial
      }
    })
  }

  gaCheckoutEvent() {
    const bookingId = this.$route.params.id
    const checkoutStorageKey = `booking#${bookingId}-checkout-fired`
    const checkoutAlreadyFired = localStorage.getItem(checkoutStorageKey)

    if (checkoutAlreadyFired !== 'true') {
      localStorage.setItem(checkoutStorageKey, 'true')
      this.$store.dispatch(BookingsActionTypes.TrackCheckout, bookingId)
    }
  }
}
